import { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { canWrite, supabase } from '../lib/supabase'

const MARQUEE_WORDS = [
  'say something real', 'your perspective matters', 'no algorithm here',
  'mean it', 'be heard', 'write it down', 'depth over reach', 'your voice',
  'built for the ones who think', 'opinions that land',
  'not optimised for virality.', 'the margins',
]

const BELONGS = [
  { num: '01', title: 'education, examined', eg: '"exams test memory, not thinking" · "what school is actually optimising for" · "the gap between curriculum and the real world"' },
  { num: '02', title: 'what social media buries', eg: '"the posts you write and never publish" · "why depth gets fewer likes than noise" · "what we\'re losing by optimising for engagement"' },
  { num: '03', title: 'politics, without the performance', eg: '"decisions made about us, without us" · "what the news leaves out" · "why young people are angrier than they\'re given credit for"' },
  { num: '04', title: 'culture, argued properly', eg: '"that album reframed how i think" · "what this film is actually saying" · "we confuse loudness for relevance"' },
  { num: '05', title: 'perspectives that don\'t fit the room', eg: '"too intense for my friend group" · "too young to be taken seriously" · "the opinion that doesn\'t fit in 280 characters"' },
  { num: '06', title: 'the world as you\'re inheriting it', eg: '"the problems being handed to our generation" · "what adults aren\'t saying out loud" · "what responsibility actually looks like at 17"' },
  { num: '07', title: 'the record you want to set straight', eg: '"what gen z actually stands for" · "we\'re not apathetic — we\'re exhausted" · "the assumptions that need challenging"' },
  { num: '08', title: 'the questions that don\'t go away', eg: '"what actually makes a life worthwhile" · "whether any of this matters" · "the thing you\'ve been thinking about for months"' },
]

const MANIFESTO = [
  { n: '01', text: 'your perspective doesn\'t need a credential.', sub: 'you don\'t need a platform, a following, or a title. you need a point of view and the willingness to back it up.' },
  { n: '02', text: 'reach is not the same as impact.', sub: 'a piece that changes how one person thinks is worth more than content that a thousand people scroll past. we\'re optimising for the former.' },
  { n: '03', text: 'disagreement is the point.', sub: 'this isn\'t a space for consensus. push back. challenge. argue from evidence. the only thing that doesn\'t belong here is emptiness.' },
  { n: '04', text: 'social media wasn\'t built for this.', sub: 'short-form, algorithm-driven, optimised for reactions — it rewards the loudest take, not the most considered one. that\'s the gap we\'re filling.' },
]

const FOUNDING_TOTAL = 100

function useReveal() {
  const ref = useRef(null)
  const [visible, setVisible] = useState(false)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setVisible(true); obs.disconnect() }
    }, { threshold: 0.08 })
    obs.observe(el)
    return () => obs.disconnect()
  }, [])
  return [ref, visible]
}

function Reveal({ children, style = {}, delay = 0 }) {
  const [ref, visible] = useReveal()
  return (
    <div ref={ref} style={{
      opacity: visible ? 1 : 0,
      transform: visible ? 'none' : 'translateY(20px)',
      transition: `opacity .6s ease ${delay}ms, transform .6s ease ${delay}ms`,
      ...style,
    }}>
      {children}
    </div>
  )
}

function useCountUp(target, delay = 1200) {
  const [count, setCount] = useState(0)
  useEffect(() => {
    if (target === null) return
    const timer = setTimeout(() => {
      let c = 0
      const step = () => {
        c += Math.ceil((target - c) / 8)
        setCount(Math.min(c, target))
        if (c < target) requestAnimationFrame(step)
      }
      requestAnimationFrame(step)
    }, delay)
    return () => clearTimeout(timer)
  }, [target, delay])
  return count
}

// Trim body text for feed preview cards
function excerpt(body, len = 120) {
  if (!body) return ''
  const plain = body.replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim()
  return plain.length > len ? plain.slice(0, len).replace(/\s+\S*$/, '') + '…' : plain
}

export default function HomePage() {
  const { user, profile } = useAuth()

  // ── DB state ──────────────────────────────────────────────
  const [voiceTarget, setVoiceTarget] = useState(null)
  const [feedPosts, setFeedPosts] = useState([])
  const [foundingUsed, setFoundingUsed] = useState(null)

  // Intersection-based UI state
  const [zagLit, setZagLit] = useState(false)
  const zagRef = useRef(null)
  const [hoveredPost, setHoveredPost] = useState(null)

  // ── Fetch: writer count ────────────────────────────────────
  useEffect(() => {
    supabase
      .from('profiles')
      .select('id', { count: 'exact', head: true })
      .neq('role', 'viewer')
      .then(({ count, error }) => {
        if (!error && count !== null) setVoiceTarget(count)
        else setVoiceTarget(0)
      })
  }, [])

  // ── Fetch: 4 published feed posts ─────────────────────────
  useEffect(() => {
    supabase
      .from('posts')
      .select('id, title, tag, excerpt, body, created_at, author_id, profiles!posts_author_id_fkey(display_name)')
      .eq('status', 'published')
      .order('created_at', { ascending: false })
      .limit(4)
      .then(({ data, error }) => {
        if (!error && data) setFeedPosts(data)
      })
  }, [])

  // ── Fetch: founding spots used ────────────────────────────
  useEffect(() => {
    supabase
      .from('profiles')
      .select('id', { count: 'exact', head: true })
      .eq('is_founding', true)
      .then(({ count, error }) => {
        if (!error && count !== null) setFoundingUsed(count)
        else setFoundingUsed(0)
      })
  }, [])

  // ── Animated counts ───────────────────────────────────────
  const count = useCountUp(voiceTarget, 1200)
  const spotsRemaining = foundingUsed !== null ? Math.max(0, FOUNDING_TOTAL - foundingUsed) : FOUNDING_TOTAL

  return (
    <div style={{ background: 'var(--paper)', minHeight: '100vh', overflowX: 'hidden', paddingTop: '4.5rem' }}>

      {/* ── HERO ─────────────────────────────────────────────── */}
      <section style={s.hero} className="hero">
        <div style={s.marginLine} />
        <div style={s.scratch1} className="hero-scratch-1" />
        <div style={s.scratch2} className="hero-scratch-2" />
        <div style={s.heroBg} aria-hidden>Margins</div>

        {/* Two-column hero: left = hed + buttons + stats, right = sub copy */}
        <div style={s.heroGrid} className="hero-grid">
          {/* Left column */}
          <div style={s.heroInner}>
            <p style={s.eyebrow}>your voice, unfiltered</p>

            <h1 style={s.hed}>
              the place<br />
              for everything<br />
              you{' '}
              <span style={s.hedAccent} className="glitch-word">
                never said<span style={{ color: 'var(--ink)' }}>.</span>
              </span>
            </h1>

            <div style={s.heroActions}>
              {user && canWrite(profile?.role) ? (
                <Link to="/write" style={s.btnMain} className="btn-main-hover">start writing</Link>
              ) : (
                <Link to="/auth" style={s.btnMain} className="btn-main-hover">start writing</Link>
              )}
              <Link to="/feed" style={s.btnFeed} className="btn-feed-hover">read the feed →</Link>
            </div>

            {/* Stats row */}
            <div style={s.statsGrid} className="hero-stats-grid">
              {[
                { num: voiceTarget === null ? '…' : count, label: 'voices and counting', rust: true },
                { num: 12, label: 'ranks to climb' },
                { num: 1, label: 'rule. mean it.', rust: true },
                { num: 0, label: 'ragebait. ever.' },
              ].map(({ num, label, rust }, i) => (
                <div key={i} style={s.statCell} className="stat-cell-hover">
                  <div style={{ ...s.statNum, ...(rust ? { color: 'var(--accent)' } : {}) }}>{num}</div>
                  <div style={s.statLabel}>{label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right column — editorial sub copy */}
          <div style={s.heroRight} className="hero-right">
            <p style={s.sub}>
              not built for virality.<br />
              not optimised for reactions.<br />
              <strong style={{ color: 'var(--muted)', fontWeight: 400 }}>built for the argument.</strong>
            </p>
            <p style={{ ...s.sub, marginTop: '1.5rem', marginBottom: 0 }}>
              your perspective, published —<br />
              and readers who actually<br />engage with it.
            </p>
          </div>
        </div>

        <div style={s.scrollInd}>
          <span style={s.scrollDot} className="scroll-dot-anim" />
          scroll
        </div>
      </section>

      {/* ── MARQUEE ──────────────────────────────────────────── */}
      <div style={s.marqueeWrap}>
        <div style={s.marqueeTrack} className="marquee-run">
          {[...MARQUEE_WORDS, ...MARQUEE_WORDS].map((w, i) => (
            <span key={i} style={s.marqueeItem}>{w}<span style={s.marqueeDot}>◆</span></span>
          ))}
        </div>
      </div>

      <div style={s.cut} />

      {/* ── FEED PREVIEW ─────────────────────────────────────── */}
      {feedPosts.length > 0 && (
        <>
          <section style={s.feedSection}>
            <div style={s.feedInner}>
              <Reveal>
                <div style={s.feedTopRow} className="feed-top-row">
                  <div>
                    <p style={s.sectionTag}>from the feed</p>
                    <h2 style={s.feedHed}>what people are writing.</h2>
                  </div>
                  <Link to="/feed" style={s.feedSeeAll}>see everything →</Link>
                </div>
              </Reveal>
              <div style={s.feedGrid} className="feed-preview-grid">
                {feedPosts.map((post, i) => (
                  <Reveal key={post.id} delay={i * 80}>
                    <Link
                      to={`/post/${post.id}`}
                      style={{
                        ...s.feedCard,
                        ...(hoveredPost === post.id ? s.feedCardHover : {}),
                      }}
                      onMouseEnter={() => setHoveredPost(post.id)}
                      onMouseLeave={() => setHoveredPost(null)}
                    >
                      {post.tag && (
                        <span style={s.feedTag}>{post.tag}</span>
                      )}
                      <p style={s.feedCardTitle}>{post.title}</p>
                      <p style={s.feedCardExcerpt}>
                        {post.excerpt || excerpt(post.body)}
                      </p>
                      <div style={s.feedCardMeta}>
                        <span style={s.feedCardAuthor}>
                          {post.profiles?.display_name || 'anonymous'}
                        </span>
                        <span style={s.feedCardDate}>
                          {new Date(post.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                        </span>
                      </div>
                    </Link>
                  </Reveal>
                ))}
              </div>
            </div>
          </section>
          <div style={s.cut} />
        </>
      )}

      {/* ── WHY THIS EXISTS ──────────────────────────────────── */}
      <section style={s.s2}>
        <div style={s.xmark} aria-hidden />
        <div style={s.s2Inner}>
          <Reveal><p style={s.sectionTag}>why this exists</p></Reveal>
          <Reveal delay={60}><p style={s.s2p}>social media wasn't built for depth. it was built for speed — short-form, high-volume, <strong style={s.strongLight}>optimised for the algorithm, not for the argument.</strong></p></Reveal>
          <Reveal delay={120}><p style={s.s2p}>if your take doesn't fit in a caption, it gets buried. if your opinion is too considered, too long, too nuanced — there's nowhere for it to go.</p></Reveal>
          <Reveal delay={180}><p style={s.s2p}>you don't get rewarded for being right. you get rewarded for being fast, for being reactive, for saying what the crowd already wants to hear.</p></Reveal>
          <Reveal delay={240}><p style={s.s2p}>so the real opinions — the ones with actual thought behind them — <strong style={s.strongLight}>don't make it out.</strong></p></Reveal>
          <Reveal delay={300}>
            <p style={s.s2Big}>
              that's{' '}
              <span ref={zagRef} style={{ position: 'relative', display: 'inline-block' }}>
                <span style={{ position: 'relative' }}>
                  why
                  <span style={{
                    position: 'absolute', bottom: -3, left: 0, right: 0,
                    height: 3, background: 'var(--accent)',
                    transformOrigin: 'left',
                    transform: zagLit ? 'scaleX(1)' : 'scaleX(0)',
                    transition: 'transform .4s ease',
                    display: 'block',
                  }} />
                </span>
              </span>{' '}
              this exists.
            </p>
          </Reveal>
          <Reveal delay={360}>
            <div style={s.ruleBox} className="rule-box-hover">
              <div style={s.ruleTab}><span style={s.ruleTabText}>the rule</span></div>
              <div style={s.ruleContent}>
                <p style={s.ruleMain}>mean it. that's it.</p>
                <p style={s.ruleSub}>opinions with zero reasoning don't belong here. everything else does.</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <div style={s.cut} />

      {/* ── WHAT BELONGS HERE ────────────────────────────────── */}
      <section style={s.belongs}>
        <Reveal><p style={{ ...s.sectionTag, paddingLeft: '2.8rem' }}>what belongs here</p></Reveal>
        <Reveal delay={60}>
          <h2 style={{ ...s.belongsHed, paddingLeft: '2.8rem' }}>
            if it deserves a proper argument,<br />it belongs here.
          </h2>
        </Reveal>
        <div style={s.belongsGrid} className="belongs-grid">
          {BELONGS.map((b, i) => (
            <Reveal key={i} delay={i * 60} style={{ height: '100%' }}>
              <div style={s.bcard} className="bcard-hover">
                <div style={s.bcardIcon}>{b.num}</div>
                <div style={s.bcardTitle}>{b.title}</div>
                <div style={s.bcardEg}>{b.eg}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <div style={s.cut} />

      {/* ── MANIFESTO ────────────────────────────────────────── */}
      <section style={s.manifesto}>
        <div style={s.manifestoLine} />
        <div style={s.manifestoInner}>
          <Reveal><p style={s.sectionTag}>what we believe</p></Reveal>
          {MANIFESTO.map((m, i) => (
            <Reveal key={i} delay={i * 80}>
              <div style={s.mitem} className="mitem-hover">
                <span style={s.mitemNum}>{m.n}</span>
                <div>
                  <p style={s.mitemText}>{m.text}</p>
                  <p style={s.mitemSub}>{m.sub}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <div style={s.cut} />

      {/* ── COMMUNITY CTA ────────────────────────────────────── */}
      <section style={s.s4}>
        <div style={s.s4Ghost} aria-hidden>PRESS</div>
        <div style={s.s4Inner}>
          <div style={s.s4Top} className="s4-top-grid">
            <div>
              <Reveal><p style={s.sectionTag}>the press — rank system</p></Reveal>
              <Reveal delay={60}><h2 style={s.s4Hed}>the more you say,<br />the more you earn.</h2></Reveal>
              <Reveal delay={120}><p style={s.s4Sub}>not followers. not likes. recognition for showing up with something to say. twelve ranks, each one earned. the voice is by appointment — you can't buy it.</p></Reveal>
              <Reveal delay={180}>
                <Link to="/community" style={{ ...s.btnMain, marginTop: '1.5rem', display: 'inline-block' }} className="btn-main-hover">
                  see how it works →
                </Link>
              </Reveal>
            </div>
            <Reveal>
              <div style={s.pressCallout} className="press-callout-hover">
                <p style={s.pcLabel}>founding members</p>
                <p style={s.pcNum}>100</p>
                <p style={s.pcDesc}>first 100 members get a permanent founding badge. once they're gone, they're gone. sign up and write — that's it.</p>
                <div style={{ marginTop: '1rem' }}>
                  {user ? (
                    <Link to="/write" style={{ fontFamily: "'DM Mono', monospace", fontSize: '.72rem', letterSpacing: '.12em', color: 'var(--accent)', textDecoration: 'none' }}>start writing →</Link>
                  ) : (
                    <Link to="/auth" style={{ fontFamily: "'DM Mono', monospace", fontSize: '.72rem', letterSpacing: '.12em', color: 'var(--accent)', textDecoration: 'none' }}>claim your spot →</Link>
                  )}
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

    </div>
  )
}

const s = {
  hero: {
    height: 'calc(100vh - 4.5rem)',
    display: 'flex', flexDirection: 'column',
    justifyContent: 'center', padding: '2rem 2rem 1rem',
    position: 'relative', overflow: 'hidden',
  },
  heroGrid: {
    display: 'grid',
    gridTemplateColumns: '1.1fr .9fr',
    gap: '2.25rem',
    alignItems: 'center',
    paddingLeft: '2.8rem',
    maxWidth: 1180,
    flex: 1,
  },
  heroRight: {
    borderLeft: '2px solid var(--rule)',
    paddingLeft: '2rem',
    opacity: 0,
    animation: 'fup .6s ease 1s forwards',
  },
  marginLine: {
    position: 'absolute', left: '2rem', top: 0, bottom: 0,
    width: 2, background: 'var(--accent)', opacity: .5,
    transformOrigin: 'top', animation: 'drawml 1.2s cubic-bezier(.4,0,.2,1) forwards',
  },
  scratch1: {
    position: 'absolute', left: '2rem', top: '3.5rem',
    height: 2, background: 'var(--accent)', opacity: .3,
    width: 0, animation: 'scratchin .6s ease .8s forwards',
  },
  scratch2: {
    position: 'absolute', left: '2rem', top: '4.3rem',
    height: 1, background: 'var(--accent)', opacity: .15,
    width: 0, animation: 'scratchin .4s ease 1s forwards',
  },
  heroBg: {
    position: 'absolute', right: '-1rem', top: '50%',
    transform: 'translateY(-52%)',
    fontFamily: "'Playfair Display', Georgia, 'Times New Roman', serif",
    fontWeight: 700,
    fontSize: 'clamp(6rem,18vw,15rem)',
    color: 'var(--hero-ghost)', lineHeight: .9,
    letterSpacing: '-.02em',
    pointerEvents: 'none', userSelect: 'none',
    animation: 'bgdrift 8s ease-in-out infinite',
  },
  heroInner: { position: 'relative', zIndex: 1 },
  eyebrow: {
    fontFamily: "'DM Mono', monospace", fontSize: '.68rem',
    letterSpacing: '.2em', color: 'var(--accent)', marginBottom: '.7rem',
    opacity: 0, animation: 'fup .5s ease .2s forwards',
  },
  hed: {
    fontFamily: "'Space Grotesk', sans-serif",
    fontSize: 'clamp(2.2rem,5.5vw,4.8rem)', fontWeight: 700,
    lineHeight: .9, letterSpacing: '-.03em', color: 'var(--ink)',
    marginBottom: '1.2rem', opacity: 0, animation: 'fup .6s ease .4s forwards',
  },
  hedAccent: { color: 'var(--accent)', display: 'inline-block' },
  sub: {
    fontSize: '.95rem', color: 'var(--muted)', lineHeight: 1.75,
    maxWidth: '26ch',
  },
  heroActions: {
    display: 'flex', gap: '.6rem', alignItems: 'center',
    flexWrap: 'wrap', marginBottom: '1.2rem',
    opacity: 0, animation: 'fup .6s ease .8s forwards',
  },
  btnMain: {
    fontFamily: "'Space Grotesk', sans-serif", fontSize: '.95rem',
    fontWeight: 700, letterSpacing: '-.01em',
    color: 'var(--paper)', background: 'var(--ink)',
    padding: '.7rem 1.8rem', border: 'none', cursor: 'pointer',
    transition: 'all .25s', display: 'inline-block', textDecoration: 'none',
  },
  btnSec: {
    fontFamily: "'Space Grotesk', sans-serif", fontSize: '.9rem', fontWeight: 700,
    letterSpacing: '-.01em', color: 'var(--ink)',
    background: 'none', border: '2px solid var(--rule)',
    padding: '.65rem 1.6rem',
    cursor: 'pointer', transition: 'all .2s',
    textDecoration: 'none', display: 'inline-block',
  },
  btnFeed: {
    fontFamily: "'Space Grotesk', sans-serif", fontSize: '.95rem',
    fontWeight: 700, letterSpacing: '-.01em',
    color: 'var(--paper)', background: 'var(--accent)',
    padding: '.7rem 1.8rem', border: 'none', cursor: 'pointer',
    transition: 'all .25s', display: 'inline-block', textDecoration: 'none',
  },
  statsGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(4,1fr)',
    gap: 1, background: 'var(--rule)', border: '1px solid var(--rule)',
    opacity: 0, animation: 'fup .6s ease 1s forwards',
  },
  statCell: {
    background: 'var(--paper)', padding: '.75rem .8rem',
    transition: 'background .2s',
  },
  statNum: {
    fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700,
    fontSize: 'clamp(1.2rem,3vw,1.8rem)', color: 'var(--ink)',
    letterSpacing: '-.03em', lineHeight: 1, marginBottom: '.2rem',
  },
  statLabel: {
    fontFamily: "'DM Mono', monospace", fontSize: '.66rem',
    letterSpacing: '.1em', color: 'var(--muted)',
  },
  scrollInd: {
    marginTop: 'auto', paddingTop: '.75rem', paddingLeft: '2.8rem',
    display: 'flex', alignItems: 'center', gap: '.6rem',
    fontFamily: "'DM Mono', monospace", fontSize: '.66rem',
    letterSpacing: '.14em', color: 'var(--muted)',
    opacity: 0, animation: 'fup 1s ease 1.4s both',
  },
  scrollDot: {
    width: 5, height: 5, borderRadius: '50%',
    background: 'var(--accent)', display: 'inline-block',
    animation: 'scrollbounce 1.5s ease-in-out infinite 2s',
  },
  marqueeWrap: {
    background: 'var(--accent)', padding: '.55rem 0',
    overflow: 'hidden', borderTop: '1px solid #a83214',
    borderBottom: '1px solid #a83214',
  },
  marqueeTrack: {
    display: 'flex', animation: 'marquee 22s linear infinite',
    width: 'max-content',
  },
  marqueeItem: {
    fontFamily: "'DM Mono', monospace", fontSize: '.76rem',
    letterSpacing: '.14em', color: 'var(--paper)',
    padding: '0 1.5rem', whiteSpace: 'nowrap',
    display: 'flex', alignItems: 'center', gap: '1.5rem',
  },
  marqueeDot: { fontSize: '.64rem', opacity: .6 },
  cut: { height: 1, background: 'var(--rule)' },
  s2: { padding: '5rem 2rem', position: 'relative', overflow: 'hidden' },
  xmark: {
    position: 'absolute', right: '3rem', top: '3rem',
    width: 40, height: 40, opacity: .06,
    animation: 'xspin 25s linear infinite',
  },
  s2Inner: {
    paddingLeft: '2.8rem', borderLeft: '2px solid var(--rule)',
    maxWidth: 600,
  },
  sectionTag: {
    fontFamily: "'DM Mono', monospace", fontSize: '.68rem',
    letterSpacing: '.2em', color: 'var(--accent)', marginBottom: '1.8rem',
  },
  s2p: {
    fontSize: '1.1rem', lineHeight: 1.9, color: 'var(--muted)',
    marginBottom: '.9rem',
  },
  strongLight: { color: '#c8b8a8', fontWeight: 400 },
  s2Big: {
    fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700,
    fontSize: 'clamp(1.8rem,5vw,3rem)', color: 'var(--ink)',
    lineHeight: 1, marginTop: '2rem', letterSpacing: '-.02em',
  },
  ruleBox: {
    marginTop: '2.8rem', display: 'inline-flex', alignItems: 'stretch',
    border: '1px solid #2a1f1a', transition: 'border-color .2s',
  },
  ruleTab: {
    background: 'var(--accent)', padding: '.65rem .85rem',
    display: 'flex', alignItems: 'center',
  },
  ruleTabText: {
    fontFamily: "'DM Mono', monospace", fontSize: '.62rem',
    letterSpacing: '.18em', color: 'var(--paper)',
    writingMode: 'vertical-lr', transform: 'rotate(180deg)',
  },
  ruleContent: { padding: '.9rem 1.3rem' },
  ruleMain: {
    fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700,
    fontSize: '1.5rem', color: 'var(--ink)', letterSpacing: '-.01em', lineHeight: 1,
  },
  ruleSub: {
    fontFamily: "'DM Mono', monospace", fontSize: '.66rem',
    color: 'var(--muted)', letterSpacing: '.08em', marginTop: '.4rem',
  },
  belongs: { padding: '4rem 2rem', background: 'var(--cream)', borderTop: '1px solid var(--rule)' },
  belongsHed: {
    fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700,
    fontSize: 'clamp(1.4rem,4vw,2.2rem)', color: 'var(--ink)',
    letterSpacing: '-.02em', marginBottom: '2.5rem', lineHeight: 1.1,
  },
  belongsGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(260px,1fr))',
    gap: 1, background: 'var(--rule)',
  },
  bcard: {
    background: 'var(--paper)', padding: '1.6rem 1.5rem',
    borderTop: '2px solid transparent', transition: 'all .2s',
    cursor: 'default',
  },
  bcardIcon: {
    fontFamily: "'Bebas Neue', sans-serif", fontSize: '2.2rem',
    color: 'var(--accent)', opacity: .35, lineHeight: 1, marginBottom: '.8rem',
    transition: 'opacity .2s',
  },
  bcardTitle: {
    fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700,
    fontSize: '.95rem', color: 'var(--ink)', marginBottom: '.5rem',
    letterSpacing: '-.01em', lineHeight: 1.2,
  },
  bcardEg: {
    fontFamily: "'DM Mono', monospace", fontSize: '.76rem',
    letterSpacing: '.06em', color: 'var(--muted)', lineHeight: 1.65,
  },
  manifesto: {
    padding: '5rem 2rem', borderTop: '1px solid var(--rule)',
    background: 'var(--cream)', position: 'relative', overflow: 'hidden',
  },
  manifestoLine: {
    position: 'absolute', left: '2rem', top: 0, bottom: 0,
    width: 2, background: 'var(--accent)', opacity: .2,
  },
  manifestoInner: { paddingLeft: '2.8rem', maxWidth: 660 },
  mitem: {
    padding: '1.4rem 0', borderBottom: '1px solid var(--rule)',
    display: 'flex', alignItems: 'flex-start', gap: '1.2rem',
    transition: 'all .2s', cursor: 'default',
  },
  mitemNum: {
    fontFamily: "'Bebas Neue', sans-serif", fontSize: '2.5rem',
    color: 'var(--accent)', lineHeight: .9, flexShrink: 0,
    transition: 'all .2s', width: 36,
  },
  mitemText: {
    fontFamily: "'Space Grotesk', sans-serif", fontSize: '1.1rem',
    fontWeight: 700, color: 'var(--muted)', lineHeight: 1.4,
    letterSpacing: '-.01em', transition: 'color .2s', paddingTop: '.2rem',
  },
  mitemSub: {
    fontSize: '.85rem', color: 'var(--muted)', fontWeight: 400,
    marginTop: '.3rem', lineHeight: 1.55, transition: 'color .2s',
  },
  // Feed preview section
  feedSection: {
    padding: '5rem 2rem', borderTop: '1px solid var(--rule)',
    background: 'var(--paper)',
  },
  feedInner: { paddingLeft: '2.8rem' },
  feedTopRow: {
    display: 'flex', alignItems: 'flex-end',
    justifyContent: 'space-between', marginBottom: '2.5rem',
    flexWrap: 'wrap', gap: '1rem',
  },
  feedHed: {
    fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700,
    fontSize: 'clamp(1.6rem,4vw,2.8rem)', color: 'var(--ink)',
    letterSpacing: '-.02em', lineHeight: .95, marginTop: '.4rem',
  },
  feedSeeAll: {
    fontFamily: "'DM Mono', monospace", fontSize: '.72rem',
    letterSpacing: '.14em', color: 'var(--accent)',
    textDecoration: 'none', transition: 'opacity .2s',
    flexShrink: 0,
  },
  feedGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
    gap: 1, background: 'var(--rule)',
  },
  feedCard: {
    background: 'var(--paper)', padding: '1.5rem',
    borderTop: '2px solid transparent',
    transition: 'border-color .2s, background .2s',
    display: 'block', textDecoration: 'none', cursor: 'pointer',
  },
  feedCardHover: {
    borderTopColor: 'var(--accent)',
    background: 'var(--cream)',
  },
  feedTag: {
    fontFamily: "'DM Mono', monospace", fontSize: '.6rem',
    letterSpacing: '.18em', color: 'var(--accent)',
    display: 'inline-block', marginBottom: '.75rem',
    textTransform: 'lowercase',
  },
  feedCardTitle: {
    fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700,
    fontSize: '1rem', color: 'var(--ink)',
    letterSpacing: '-.01em', lineHeight: 1.25,
    marginBottom: '.6rem',
  },
  feedCardExcerpt: {
    fontSize: '.82rem', color: 'var(--muted)',
    lineHeight: 1.6, marginBottom: '1rem',
  },
  feedCardMeta: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    borderTop: '1px solid var(--rule)', paddingTop: '.75rem',
  },
  feedCardAuthor: {
    fontFamily: "'DM Mono', monospace", fontSize: '.64rem',
    letterSpacing: '.1em', color: 'var(--muted)',
  },
  feedCardDate: {
    fontFamily: "'DM Mono', monospace", fontSize: '.62rem',
    letterSpacing: '.1em', color: 'var(--muted)',
  },
  // Press section
  s4: {
    padding: '5rem 2rem', borderTop: '1px solid var(--rule)',
    position: 'relative', overflow: 'hidden',
  },
  s4Ghost: {
    position: 'absolute', right: '-1rem', top: '50%',
    transform: 'translateY(-50%)',
    fontFamily: "'Bebas Neue', sans-serif",
    fontSize: 'clamp(5rem,14vw,11rem)',
    color: 'rgba(255,255,255,.02)', lineHeight: 1,
    pointerEvents: 'none', userSelect: 'none',
    animation: 'bgdrift 10s ease-in-out infinite reverse',
  },
  s4Inner: { paddingLeft: '2.8rem', position: 'relative', zIndex: 1 },
  s4Top: {
    display: 'grid', gridTemplateColumns: '1fr 1fr',
    gap: '3rem', marginBottom: '3rem', alignItems: 'start',
  },
  s4Hed: {
    fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700,
    fontSize: 'clamp(1.8rem,5vw,3.2rem)', lineHeight: .95,
    color: 'var(--ink)', letterSpacing: '-.02em', marginBottom: '.6rem',
  },
  s4Sub: { fontSize: '.9rem', color: 'var(--muted)', maxWidth: '36ch', lineHeight: 1.7 },
  pressCallout: {
    border: '1px solid #2a1f1a', padding: '1.5rem',
    background: 'var(--cream)', transition: 'border-color .2s',
  },
  pcLabel: {
    fontFamily: "'DM Mono', monospace", fontSize: '.68rem',
    letterSpacing: '.14em', color: 'var(--muted)', marginBottom: '.75rem',
  },
  pcNum: {
    fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700,
    fontSize: '2.8rem', color: 'var(--accent)', letterSpacing: '-.03em',
    lineHeight: 1, marginBottom: '.4rem',
  },
  pcDesc: { fontSize: '.84rem', color: 'var(--muted)', lineHeight: 1.55 },
  tiersBox: { display: 'flex', flexDirection: 'column', border: '1px solid var(--rule)' },
  tier: {
    display: 'flex', alignItems: 'center', gap: '.75rem',
    padding: '.55rem .75rem', borderBottom: '1px solid #151210',
    cursor: 'default', transition: 'background .15s',
  },
  tierVoice: {},
  tierHover: { background: 'rgba(200,64,26,.04)' },
  tnum: {
    fontFamily: "'DM Mono', monospace", fontSize: '.6rem',
    color: 'var(--muted)', minWidth: 16,
  },
  tname: {
    fontFamily: "'Space Grotesk', sans-serif", fontSize: '.68rem',
    fontWeight: 500, color: 'var(--muted)', minWidth: 150,
    letterSpacing: '-.01em', transition: 'color .2s',
  },
  tci: {
    fontFamily: "'DM Mono', monospace", fontSize: '.6rem',
    color: 'var(--muted)', marginLeft: 'auto', transition: 'color .2s',
  },
  // Founding section
  s5: {
    padding: '5rem 2rem', borderTop: '1px solid var(--rule)',
    background: 'var(--cream)', position: 'relative', overflow: 'hidden',
  },
  s5Bg: {
    position: 'absolute', right: '-2rem', bottom: '-3rem',
    fontFamily: "'Bebas Neue', sans-serif",
    fontSize: 'clamp(6rem,16vw,12rem)',
    color: 'rgba(255,255,255,.02)', lineHeight: .9,
    pointerEvents: 'none', userSelect: 'none',
  },
  s5Inner: { paddingLeft: '2.8rem', position: 'relative', zIndex: 1 },
  s5Hed: {
    fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700,
    fontSize: 'clamp(2rem,6vw,4rem)', color: 'var(--ink)',
    lineHeight: .95, marginBottom: '.8rem', letterSpacing: '-.02em',
  },
  s5Sub: {
    fontSize: '1rem', color: 'var(--muted)', maxWidth: '48ch',
    lineHeight: 1.75, marginBottom: '3rem',
  },
  foundingRow: {
    display: 'grid', gridTemplateColumns: 'repeat(3,1fr)',
    gap: 1, background: 'var(--rule)', border: '1px solid var(--rule)',
  },
  fcell: {
    background: 'var(--paper)', padding: '1.5rem',
    transition: 'background .2s', cursor: 'default',
  },
  fcellNum: {
    fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700,
    fontSize: '2.5rem', color: 'var(--accent)',
    letterSpacing: '-.03em', lineHeight: 1, marginBottom: '.35rem',
  },
  fcellLabel: {
    fontFamily: "'DM Mono', monospace", fontSize: '.64rem',
    letterSpacing: '.14em', color: 'var(--muted)', marginBottom: '.4rem',
  },
  fcellDesc: { fontSize: '.84rem', color: 'var(--muted)', lineHeight: 1.5 },
}
